import { useState } from 'react';
import { Trophy } from 'lucide-react';
import { EmptyState, ErrorState, Skeleton, SubBar } from '@/design/components';
import { useMe } from '@/auth/useMe';
import { useCompetitions, useLeaderboard, useStandings } from './api';

/**
 * טבלת המובילים (PRD 5.8.2).
 *
 * ברירת המחדל היא החודש הנוכחי לכל הרשת. מעבר לסניף שלי הוא מתג
 * אחד, כי בסניף קטן "מקום שביעי ברשת" לא אומר כלום ו"מקום שני בסניף"
 * כן.
 */
export function LeaderboardPage() {
  const { me } = useMe();
  const [branchOnly, setBranchOnly] = useState(false);
  const branchId = branchOnly ? (me?.primaryBranchId ?? null) : null;

  const board = useLeaderboard(branchId);
  const competitions = useCompetitions();

  // התחרות הפעילה, אם יש
  const open = (competitions.data ?? []).find(
    (c) => new Date(c.starts_at) <= new Date() && new Date(c.ends_at) > new Date(),
  );
  const standings = useStandings(open?.id ?? null);

  if (board.isPending) return <Skeleton className="m-4 h-40" />;
  if (board.isError) return <ErrorState onRetry={() => void board.refetch()} />;

  const rows = board.data ?? [];

  return (
    <div className="grid content-start gap-4">
      <SubBar title="טבלת המובילים" subtitle="הנקודות שנצברו החודש" />

      <div className="grid gap-6 px-4 pb-8">
        {open && standings.data && (
          <section className="grid gap-2 rounded-md border border-olive bg-olive-soft p-3">
            <h2 className="m-0 flex items-center gap-2 font-display text-display-m font-semibold">
              <Trophy size={20} strokeWidth={1.75} aria-hidden="true" />
              {open.title}
            </h2>
            {open.prize && <p className="m-0 text-body-sm">הפרס: {open.prize}</p>}

            {standings.data.scope === 'branch' ? (
              <Rows
                items={standings.data.branches.map((b, i) => ({
                  key: b.branch_id,
                  place: i + 1,
                  name: b.name,
                  meta: null,
                  points: b.points,
                  isMe: false,
                }))}
              />
            ) : (
              <Rows
                items={standings.data.employees.map((e, i) => ({
                  key: e.employee_id,
                  place: i + 1,
                  name: e.name,
                  meta: e.branch,
                  points: e.points,
                  isMe: e.employee_id === me?.employeeId,
                }))}
              />
            )}
          </section>
        )}

        <div className="flex flex-wrap gap-2">
          <Toggle active={!branchOnly} onClick={() => setBranchOnly(false)}>
            כל הרשת
          </Toggle>
          {me?.primaryBranchId && (
            <Toggle active={branchOnly} onClick={() => setBranchOnly(true)}>
              הסניף שלי
            </Toggle>
          )}
        </div>

        {rows.length === 0 ? (
          <EmptyState
            title="עוד לא נצברו נקודות החודש"
            description="נקודות מגיעות מלומדות, חידונים ואישור קריאה של הודעות."
          />
        ) : (
          <Rows
            items={rows.map((row, i) => ({
              key: row.employee_id,
              place: i + 1,
              name: row.full_name,
              meta: null,
              points: row.points,
              isMe: row.employee_id === me?.employeeId,
            }))}
          />
        )}
      </div>
    </div>
  );
}

function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-11 cursor-pointer rounded-pill border px-4 text-body-sm font-medium ${
        active ? 'border-charcoal bg-charcoal text-on-charcoal' : 'border-muted bg-transparent text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function Rows({
  items,
}: {
  items: {
    key: string;
    place: number;
    name: string;
    meta: string | null;
    points: number;
    isMe: boolean;
  }[];
}) {
  return (
    <ol className="m-0 grid list-none gap-2 p-0">
      {items.map((item) => (
        <li
          key={item.key}
          className={`flex items-center gap-3 rounded-md border p-3 ${
            item.isMe ? 'border-olive bg-olive-soft' : 'border-line bg-raised'
          }`}
        >
          <span className="w-7 shrink-0 text-center font-display text-title font-bold tabular">
            {item.place}
          </span>
          <span className="grid min-w-0 grow gap-0.5">
            <span className="truncate text-body font-medium text-ink">
              {item.name}
              {item.isMe && <span className="ms-1 text-label text-muted">(את/ה)</span>}
            </span>
            {item.meta && <span className="text-label text-muted">{item.meta}</span>}
          </span>
          <span className="shrink-0 text-title font-medium tabular">{item.points}</span>
        </li>
      ))}
    </ol>
  );
}
